import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, CheckCheck, Clock, Phone, X } from 'lucide-react';
import {
  api,
  type AttendanceStatus,
  type Participation,
  type SlotKey,
} from '@/lib/api';
import { cn, formatPower } from '@/lib/utils';
import { PlayerLabel } from './PlayerLabel';
import { PlayerFlagChip } from './PlayerFlagChip';

type LocalAttendance = Record<number, AttendanceStatus | null>;

export default function AttendancePanel({
  eventId,
  participations,
  editable,
}: {
  eventId: number;
  participations: Participation[];
  editable: boolean;
}) {
  const assigned = participations.filter((p) => p.assignedRole != null);
  const [local, setLocal] = useState<LocalAttendance>({});
  const qc = useQueryClient();
  const serverRef = useRef<LocalAttendance>({});
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    const next: LocalAttendance = {};
    for (const p of assigned) next[p.playerId] = p.attendanceStatus;
    setLocal(next);
    serverRef.current = next;
  }, [participations]);

  const save = useMutation({
    mutationFn: (rows: { playerId: number; attendanceStatus: AttendanceStatus | null }[]) =>
      api.bulkAttendance(eventId, rows),
    onSuccess: (_data, rows) => {
      for (const r of rows) serverRef.current[r.playerId] = r.attendanceStatus;
      setSavedAt(Date.now());
      qc.invalidateQueries({ queryKey: ['participations', eventId] });
      qc.invalidateQueries({ queryKey: ['event', eventId] });
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['events-pending-attendance'] });
    },
  });

  // Auto-save debounced: whenever local diverges from the server snapshot,
  // push the diff after a short quiet period.
  useEffect(() => {
    if (!editable) return;
    const changed = Object.entries(local)
      .filter(([id, v]) => serverRef.current[Number(id)] !== v)
      .map(([id, v]) => ({ playerId: Number(id), attendanceStatus: v ?? null }));
    if (changed.length === 0) return;
    const t = setTimeout(() => save.mutate(changed), 400);
    return () => clearTimeout(t);
  }, [local, editable]);

  if (assigned.length === 0) return null;

  const pendingCount = assigned.filter((p) => local[p.playerId] == null).length;
  const dirty = Object.entries(local).some(
    ([id, v]) => serverRef.current[Number(id)] !== v,
  );
  const savedRecently = savedAt != null && Date.now() - savedAt < 2500;

  return (
    <section className="rounded-lg border border-amber-700/50 bg-amber-900/10 p-4">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">Attendance</h2>
          <p className="text-xs text-slate-400">
            Mark each assigned player as attended, late, or no-show.
            {pendingCount > 0 && (
              <span className="ml-2 text-amber-300">{pendingCount} still unmarked</span>
            )}
          </p>
        </div>
        {editable && (
          <span className="text-xs text-slate-400">
            {save.isPending
              ? 'Saving…'
              : dirty
                ? 'Unsaved…'
                : savedRecently
                  ? 'Saved'
                  : 'Auto-saves'}
          </span>
        )}
      </header>
      <div className="grid gap-4 md:grid-cols-2">
        {(['A', 'B'] as SlotKey[]).map((slot) => (
          <SlotAttendance
            key={slot}
            slot={slot}
            rows={assigned.filter((p) => p.assignedSlot === slot)}
            local={local}
            onChange={(playerId, v) => setLocal({ ...local, [playerId]: v })}
            onMarkAllAttended={() => {
              const next = { ...local };
              for (const p of assigned) {
                if (p.assignedSlot !== slot) continue;
                const cur = next[p.playerId];
                if (cur === 'no_show' || cur === 'late') continue;
                next[p.playerId] = 'showed';
              }
              setLocal(next);
            }}
            editable={editable}
          />
        ))}
      </div>
    </section>
  );
}

function SlotAttendance({
  slot,
  rows,
  local,
  onChange,
  onMarkAllAttended,
  editable,
}: {
  slot: SlotKey;
  rows: Participation[];
  local: LocalAttendance;
  onChange: (playerId: number, v: AttendanceStatus | null) => void;
  onMarkAllAttended: () => void;
  editable: boolean;
}) {
  const starters = rows
    .filter((p) => p.assignedRole === 'starter')
    .sort((a, b) => (a.assignedPosition ?? 0) - (b.assignedPosition ?? 0));
  const subs = rows
    .filter((p) => p.assignedRole === 'sub')
    .sort((a, b) => (a.assignedPosition ?? 0) - (b.assignedPosition ?? 0));
  const pending = rows.filter((p) => local[p.playerId] == null).length;
  const noShows = rows.filter((p) => local[p.playerId] === 'no_show').length;
  const lates = rows.filter((p) => local[p.playerId] === 'late').length;
  const fillableCount = rows.filter(
    (p) => local[p.playerId] !== 'showed' && local[p.playerId] !== 'no_show' && local[p.playerId] !== 'late',
  ).length;

  return (
    <div className="rounded border border-slate-800 bg-slate-950/40 p-3">
      <header className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          Slot {slot} <span className="text-xs font-normal text-slate-500">({rows.length})</span>
        </h3>
        <div className="flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            {pending > 0 && <span className="text-amber-300">{pending} pending</span>}
            {pending > 0 && (lates > 0 || noShows > 0) && (
              <span className="mx-1 text-slate-600">·</span>
            )}
            {lates > 0 && <span className="text-yellow-300">{lates} late</span>}
            {lates > 0 && noShows > 0 && <span className="mx-1 text-slate-600">·</span>}
            {noShows > 0 && <span className="text-red-400">{noShows} no-show</span>}
          </div>
          {editable && fillableCount > 0 && (
            <button
              onClick={onMarkAllAttended}
              title="Mark unmarked players in this slot as attended"
              className="inline-flex items-center gap-1 rounded border border-emerald-700 px-2 py-1 text-[11px] text-emerald-200 hover:bg-emerald-900/30"
            >
              <CheckCheck size={12} /> Mark all attended
            </button>
          )}
        </div>
      </header>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500">No assignments.</div>
      ) : (
        <>
          {starters.length > 0 && (
            <Group
              label="Starters"
              sumThp={starters.reduce(
                (a, p) => a + (p.player.latestSnapshot?.totalPower ?? 0),
                0,
              )}
            >
              {starters.map((p) => (
                <AttendanceRow
                  key={p.id}
                  participation={p}
                  value={local[p.playerId] ?? null}
                  onChange={(v) => onChange(p.playerId, v)}
                  editable={editable}
                />
              ))}
            </Group>
          )}
          {subs.length > 0 && (
            <Group
              label="Subs"
              sumThp={subs.reduce(
                (a, p) => a + (p.player.latestSnapshot?.totalPower ?? 0),
                0,
              )}
            >
              {subs.map((p) => (
                <AttendanceRow
                  key={p.id}
                  participation={p}
                  value={local[p.playerId] ?? null}
                  onChange={(v) => onChange(p.playerId, v)}
                  editable={editable}
                />
              ))}
            </Group>
          )}
        </>
      )}
    </div>
  );
}

function Group({
  label,
  sumThp,
  children,
}: {
  label: string;
  sumThp: number;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-2">
      <div className="mb-1 flex items-baseline justify-between text-[10px] uppercase tracking-wider text-slate-500">
        <span>{label}</span>
        <span className="tabular-nums" title="Sum of Total Hero Power">
          Σ THP {formatPower(sumThp)}
        </span>
      </div>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}

function AttendanceRow({
  participation,
  value,
  onChange,
  editable,
}: {
  participation: Participation;
  value: AttendanceStatus | null;
  onChange: (v: AttendanceStatus | null) => void;
  editable: boolean;
}) {
  return (
    <li className="flex items-center gap-2 rounded bg-slate-900/40 px-2 py-1 text-sm">
      <span className="w-5 text-xs tabular-nums text-slate-500">
        {participation.assignedPosition}
      </span>
      <PlayerLabel player={participation.player} className="min-w-0" />
      <PlayerFlagChip
        playerId={participation.player.id}
        flags={participation.player.activeFlags ?? []}
        editable={editable}
      />
      {participation.swapContactMade && (
        <span
          title="Confirmed contact with counterpart"
          className="inline-flex items-center text-emerald-300"
        >
          <Phone size={12} />
        </span>
      )}
      <span className="flex-1" />
      <span className="hidden tabular-nums text-xs text-slate-400 sm:inline" title="Total Hero Power">
        {formatPower(participation.player.latestSnapshot?.totalPower)}
      </span>
      {editable ? (
        <div className="flex gap-1">
          <button
            onClick={() => onChange('showed')}
            title="Attended"
            className={cn(
              'rounded p-1',
              value === 'showed'
                ? 'bg-emerald-600 text-white'
                : 'text-slate-500 hover:bg-slate-800 hover:text-emerald-400',
            )}
          >
            <Check size={14} />
          </button>
          <button
            onClick={() => onChange('late')}
            title="Attended late"
            className={cn(
              'rounded p-1',
              value === 'late'
                ? 'bg-yellow-500 text-slate-900'
                : 'text-slate-500 hover:bg-slate-800 hover:text-yellow-400',
            )}
          >
            <Clock size={14} />
          </button>
          <button
            onClick={() => onChange('no_show')}
            title="No-show"
            className={cn(
              'rounded p-1',
              value === 'no_show'
                ? 'bg-red-700 text-white'
                : 'text-slate-500 hover:bg-slate-800 hover:text-red-400',
            )}
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <span className="text-xs text-slate-400">
          {value === 'showed'
            ? 'Attended'
            : value === 'late'
              ? 'Late'
              : value === 'no_show'
                ? 'No-show'
                : '—'}
        </span>
      )}
    </li>
  );
}
